import React, {useMemo, useCallback} from 'react';
import {Autocomplete, ListBoxOptionType} from '@flixbus/honeycomb-react';
import {countriesList} from '@/Pages/Countries/constant/countriesList';
import {CountryCodesAutocompleteProps} from './CountryCodesAutocomplete.types.ts';

const CountryCodesAutocomplete: React.FC<CountryCodesAutocompleteProps> = ({
  countryCodes,
  onSelected,
  valid,
  errorMessage
}) => {
  const options: ListBoxOptionType[] = useMemo(
    () =>
      countriesList.map(country => ({
        title: `${country.name} (${country.country_code})`,
        value: country.country_code
      })),
    []
  );

  const selectedOptions: ListBoxOptionType[] = useMemo(
    () => options.filter(option => countryCodes.includes(String(option.value))),
    [countryCodes, options]
  );

  const toggleOption = useCallback(
    (option: ListBoxOptionType) => {
      const isSelected = selectedOptions.some(
        item => item.value === option.value
      );
      const newSelection = isSelected
        ? selectedOptions.filter(item => item.value !== option.value)
        : [...selectedOptions, option];

      onSelected(newSelection.map(item => String(item.value)));
    },
    [selectedOptions, onSelected]
  );

  const removeTag = useCallback(
    (option: ListBoxOptionType) => {
      const updatedSelection = selectedOptions.filter(
        selected => selected.value !== option.value
      );

      onSelected(updatedSelection.map(item => String(item.value)));
    },
    [selectedOptions, onSelected]
  );

  const clearAll = useCallback(() => {
    onSelected([]);
  }, [onSelected]);

  return (
    <Autocomplete
      multiple
      value={selectedOptions}
      options={options}
      comboboxProps={{
        label: 'Country Codes',
        tagProps: tag => ({
          renderTag: () => <>{tag.value}</>,
          closeProps: {
            label: `Remove ${tag.title}`,
            onClick: () => removeTag(tag)
          }
        }),
        resetProps: {label: 'Clear', onClick: clearAll},
        valid,
        infoError: errorMessage ?? '',
        'aria-label': 'Country Codes'
      }}
      listBoxProps={{
        optionProps: option => ({
          onClick: () => toggleOption(option)
        })
      }}
    />
  );
};

export default CountryCodesAutocomplete;
